import { Button } from "./Button";

/**
 * "Showing 51-100 of 312", and the two buttons that move between them.
 *
 * Built for #329's scale question. Every paginated list in this app
 * returned its first 50 rows and rendered them with nothing to say more
 * existed -- so 50 of 800 looked exactly like all 50, and no screen
 * could offer a next page because no response carried a count.
 *
 * DELIBERATELY NOT NUMBERED PAGES. Page numbers over a list ordered by
 * date are a false landmark: "page 4" is a different set of harvests
 * next week, so it cannot be remembered, linked or returned to. Next and
 * Previous say what they do and stay true.
 *
 * RENDERS NOTHING WHEN EVERYTHING FITS. A pager under a list of six
 * harvests is furniture that says "there might be more" when there is
 * not -- which is the same lie as the silence it replaces, told the
 * other way round.
 *
 * The RANGE leads, not the buttons. The number people need is how much
 * they are not looking at; the controls are how they answer it.
 */
export function Pager({ total, limit, offset, onChange, busy = false, unit = "rows" }) {
  // total is null when the endpoint does not page (GET /hives returns
  // everything). Nothing to say.
  if (total == null || limit == null) return null;
  if (total <= limit && offset === 0) return null;

  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);
  const canPrev = offset > 0;
  const canNext = last < total;

  return (
    <div
      style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "var(--space-3)", flexWrap: "wrap",
        marginTop: "var(--space-3)", paddingTop: "var(--space-3)",
        borderTop: "1px solid var(--color-border)",
      }}
    >
      <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-sm)" }}>
        Showing {first}–{last} of {total} {unit}
      </span>
      <div style={{ display: "flex", gap: "var(--space-2)" }}>
        {/* Secondary both: moving through a list is not the job the
            screen is for, and a blue Next on every table would compete
            with whatever the page's actual primary is (rule 7). */}
        <Button
          size="sm"
          variant="secondary"
          disabled={!canPrev || busy}
          onClick={() => onChange(Math.max(0, offset - limit))}
        >
          Previous
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!canNext || busy}
          onClick={() => onChange(offset + limit)}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
